/**
 * SaudiPhoneInput — two-section phone input per UI/UX:
 * 1. Non-editable: "+966"
 * 2. Editable: digits only (9 digits, leading 0 stripped)
 * Responsive, RTL-friendly, matches Input styling.
 */

import * as React from 'react'
import { cn } from '../../lib/utils'

const SAUDI_PREFIX = '+966'

const inputBaseClasses =
  'border-input h-9 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm bg-transparent'

export interface SaudiPhoneInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type'> {
  /** Digits only (9 digits, leading 0 stripped). Controlled. */
  value: string
  /** Called with digits only when user types. */
  onChange: (digits: string) => void
  /** Placeholder for the digits field (e.g. "123456789"). */
  placeholder?: string
  /** Optional error message below input. */
  error?: string
  /** Optional id for the editable input (for label association). */
  id?: string
}

const SaudiPhoneInput = React.forwardRef<HTMLInputElement, SaudiPhoneInputProps>(
  (
    {
      className,
      value,
      onChange,
      placeholder = '501234567',
      error,
      id,
      disabled,
      'aria-invalid': ariaInvalid,
      ...rest
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let next = e.target.value.replace(/\D/g, '')
      if (next.startsWith('0')) {
        next = next.slice(1)
      }
      next = next.slice(0, 9)
      onChange(next)
    }

    const inputId = id ?? rest.name

    return (
      <div className={cn('w-full', className)}>
        <div
          className={cn(
            'flex items-stretch w-full overflow-hidden rounded-md border',
            'border-input',
            'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
            error && 'border-destructive aria-invalid:ring-destructive/20',
            disabled && 'opacity-50 pointer-events-none'
          )}
          dir="ltr"
        >
          {/* Non-editable: +966 */}
          <div
            data-slot="saudi-phone-prefix"
            className={cn(
              'flex items-center gap-1.5 shrink-0 border-r border-input px-3 py-1',
              'bg-muted/50 text-foreground select-none',
              inputBaseClasses,
              'rounded-none rounded-s-md'
            )}
            aria-hidden
          >
            <span className="font-medium tabular-nums">{SAUDI_PREFIX}</span>
          </div>
          {/* Editable: digits only */}
          <input
            ref={ref}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            id={inputId}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={10}
            className={cn(
              'flex-1 min-w-0 px-3 py-1 rounded-none rounded-e-md',
              'placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-0 border-0',
              inputBaseClasses
            )}
            aria-invalid={ariaInvalid ?? !!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            data-slot="saudi-phone-input"
            {...rest}
          />
        </div>
        {error && (
          <p
            id={inputId ? `${inputId}-error` : undefined}
            className="text-xs text-destructive mt-1"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)
SaudiPhoneInput.displayName = 'SaudiPhoneInput'

export { SaudiPhoneInput, SAUDI_PREFIX }
